import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { activityAPI } from '../services/api';
import { formatDateTime } from '../utils/currency';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  Activity, 
  RefreshCw, 
  Calendar, 
  Users, 
  FileText, 
  Package, 
  Building2,
  Plus,
  Edit,
  Trash2,
  CreditCard,
  Filter,
  Clock,
  Download,
  X,
  Eye,
  User,
  MapPin,
  Phone,
  Mail,
  IndianRupee,
  Info
} from 'lucide-react';

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [deleting, setDeleting] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadActivityLogs();
      loadSummary();
    }
  }, [currentUser, filterEntity, filterAction]);

  const loadActivityLogs = async () => {
    setLoading(true);
    try {
      const entityType = filterEntity === 'all' ? null : filterEntity;
      const action = filterAction === 'all' ? null : filterAction;
      const data = await activityAPI.getLogs(100, entityType, action);
      setLogs(data);
    } catch (error) {
      console.error('Failed to load activity logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load activity logs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const data = await activityAPI.getSummary();
      setSummary(data);
    } catch (error) {
      console.error('Failed to load summary:', error);
    }
  };

  const handleDeleteLog = async (logId) => {
    setDeleting(logId);
    try {
      await activityAPI.delete(logId);
      setLogs(logs.filter(log => log.id !== logId));
      toast({
        title: 'Deleted',
        description: 'Activity log deleted successfully'
      });
      loadSummary(); // Refresh summary
    } catch (error) {
      console.error('Failed to delete activity log:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete activity log',
        variant: 'destructive'
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete all activity logs? This action cannot be undone.')) {
      return;
    }
    
    try {
      const result = await activityAPI.clearAll();
      setLogs([]);
      setSummary({ ...summary, totalLogs: 0, last24Hours: 0, byAction: {}, byEntityType: {} });
      toast({
        title: 'Cleared',
        description: `${result.count} activity logs deleted`
      });
    } catch (error) {
      console.error('Failed to clear activity logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear activity logs',
        variant: 'destructive'
      });
    }
  };

  const downloadPDF = () => {
    try {
      const doc = new jsPDF();
      
      // Helper function to sanitize text for PDF (remove special chars that don't render)
      const sanitizeForPDF = (text) => {
        if (!text) return '-';
        return text
          .replace(/₹/g, 'Rs.')  // Replace rupee symbol
          .replace(/&/g, '&')    // Fix HTML entities
          .replace(/</g, '<')
          .replace(/>/g, '>')
          .replace(/"/g, '"')
          .replace(/'/g, "'")
          .replace(/[^\x20-\x7E\s]/g, ''); // Remove non-ASCII chars
      };
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(31, 41, 55);
      doc.text('Activity Log Report', 14, 20);
      
      // Subtitle with date
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, 28);
      doc.text(`Total Records: ${logs.length}`, 14, 34);
      
      // Summary section
      if (summary) {
        doc.setFontSize(12);
        doc.setTextColor(31, 41, 55);
        doc.text('Summary', 14, 45);
        
        doc.setFontSize(10);
        doc.setTextColor(75, 85, 99);
        doc.text(`Total Activities: ${summary.totalLogs || 0}`, 14, 52);
        doc.text(`Last 24 Hours: ${summary.last24Hours || 0}`, 14, 58);
        doc.text(`Created: ${summary.byAction?.create || 0}`, 14, 64);
        doc.text(`Updated: ${summary.byAction?.update || 0}`, 70, 64);
        doc.text(`Deleted: ${summary.byAction?.delete || 0}`, 126, 64);
      }
      
      // Table data - sanitize all text fields
      const tableData = logs.map(log => [
        sanitizeForPDF(log.userName),
        sanitizeForPDF(log.userRole),
        sanitizeForPDF(log.action),
        sanitizeForPDF(log.entityType),
        sanitizeForPDF(log.entityName),
        sanitizeForPDF(log.details),
        formatDateTime(log.createdAt)
      ]);
      
      // Add table
      autoTable(doc, {
        startY: 75,
        head: [['User', 'Role', 'Action', 'Entity', 'Name', 'Details', 'Date/Time']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [20, 184, 166], // Teal
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8
        },
        bodyStyles: {
          fontSize: 7,
          textColor: [55, 65, 81]
        },
        alternateRowStyles: {
          fillColor: [240, 253, 250]
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 18 },
          2: { cellWidth: 18 },
          3: { cellWidth: 20 },
          4: { cellWidth: 30 },
          5: { cellWidth: 40 },
          6: { cellWidth: 30 }
        },
        margin: { top: 75 }
      });
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(156, 163, 175);
        doc.text(
          `Page ${i} of ${pageCount} | VenueMate Activity Log`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
      
      // Save
      const fileName = `ActivityLog_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast({
        title: 'Downloaded',
        description: `Activity log saved as ${fileName}`
      });
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate PDF',
        variant: 'destructive'
      });
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      create: <Plus className="w-4 h-4 text-green-600" />,
      update: <Edit className="w-4 h-4 text-blue-600" />,
      delete: <Trash2 className="w-4 h-4 text-red-600" />,
      payment: <CreditCard className="w-4 h-4 text-purple-600" />
    };
    return icons[action] || <Activity className="w-4 h-4 text-gray-600 dark:text-muted-foreground" />;
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      booking: <Calendar className="w-4 h-4" />,
      lead: <Users className="w-4 h-4" />,
      venue: <Building2 className="w-4 h-4" />,
      inventory: <Package className="w-4 h-4" />,
      staff: <Users className="w-4 h-4" />,
      income: <IndianRupee className="w-4 h-4" />,
      expense: <CreditCard className="w-4 h-4" />,
      transfer: <Activity className="w-4 h-4" />,
      account_head: <Building2 className="w-4 h-4" />
    };
    return icons[entityType] || <FileText className="w-4 h-4" />;
  };

  const getActionBadge = (action) => {
    const styles = {
      create: 'bg-green-100 dark:bg-green-900/30 text-green-800 border-green-200',
      update: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border-blue-200',
      delete: 'bg-red-100 dark:bg-red-900/30 text-red-800 border-red-200',
      payment: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 border-purple-200'
    };
    return styles[action] || 'bg-gray-100 dark:bg-secondary text-gray-800 dark:text-foreground border-gray-200 dark:border-border';
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800',
      manager: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800',
      staff: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800',
      stakeholder: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800'
    };
    return styles[role] || 'bg-gray-100 dark:bg-secondary text-gray-800 dark:text-foreground';
  };

  // Handle activity click to show details
  const handleActivityClick = (log) => {
    setSelectedActivity(log);
    setDetailsOpen(true);
  };

  // Parse and format changes for display
  const formatChanges = (changes) => {
    if (!changes) return null;
    
    // If changes is a string, try to parse it
    if (typeof changes === 'string') {
      try {
        changes = JSON.parse(changes);
      } catch {
        return <p className="text-sm text-gray-600 dark:text-muted-foreground">{changes}</p>;
      }
    }
    
    // If it's an object with before/after
    if (changes.before || changes.after) {
      return (
        <div className="space-y-3">
          {changes.before && (
            <div>
              <p className="text-xs font-medium text-red-600 mb-1">Before:</p>
              <pre className="text-xs bg-red-50 dark:bg-red-900/20 p-2 rounded overflow-x-auto">{JSON.stringify(changes.before, null, 2)}</pre>
            </div>
          )}
          {changes.after && (
            <div>
              <p className="text-xs font-medium text-green-600 mb-1">After:</p>
              <pre className="text-xs bg-green-50 dark:bg-green-900/20 p-2 rounded overflow-x-auto">{JSON.stringify(changes.after, null, 2)}</pre>
            </div>
          )}
        </div>
      );
    }
    
    // If it's a simple object, display key-value pairs
    return (
      <div className="space-y-1">
        {Object.entries(changes).map(([key, value]) => (
          <div key={key} className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
            <span className="text-gray-900 dark:text-foreground font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
          </div>
        ))}
      </div>
    );
  };

  // Only show for admins
  if (currentUser?.role !== 'admin') {
    return null;
  }

  return (
    <div className="space-y-3 sm:space-y-6 w-full overflow-x-hidden" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div className="flex flex-col gap-2 sm:gap-3">
        <div>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-foreground flex items-center gap-2">
            <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 flex-shrink-0" />
            <span className="truncate">Activity Log</span>
          </h2>
          <p className="text-[11px] sm:text-sm text-gray-600 dark:text-muted-foreground mt-0.5">Track team changes</p>
        </div>
        <div className="flex flex-wrap gap-1.5 sm:gap-2 w-full">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadPDF}
            disabled={logs.length === 0}
            className="text-teal-600 border-teal-300 hover:bg-teal-50 dark:bg-teal-900/20 h-8 sm:h-9 text-[10px] sm:text-sm px-2 sm:px-3 flex-1 sm:flex-none min-w-0"
          >
            <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
            <span className="truncate">PDF</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAll}
            disabled={logs.length === 0}
            className="text-red-600 border-red-300 hover:bg-red-50 dark:bg-red-900/20 h-8 sm:h-9 text-[10px] sm:text-sm px-2 sm:px-3 flex-1 sm:flex-none min-w-0"
          >
            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
            <span className="truncate">Clear</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadActivityLogs(); loadSummary(); }}
            disabled={loading}
            className="h-8 sm:h-9 text-[10px] sm:text-sm px-2 sm:px-3 flex-1 sm:flex-none min-w-0"
          >
            <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0 ${loading ? 'animate-spin' : ''}`} />
            <span className="truncate">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards - 2x2 grid */}
      {summary && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4 w-full" style={{ maxWidth: '100%' }}>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-indigo-100 overflow-hidden min-w-0">
            <CardContent className="p-2 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-indigo-200 rounded-lg flex-shrink-0">
                  <Activity className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-indigo-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base sm:text-2xl font-bold text-indigo-900 truncate">{summary.totalLogs}</p>
                  <p className="text-[9px] sm:text-xs text-indigo-600">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100 overflow-hidden min-w-0">
            <CardContent className="p-2 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-200 rounded-lg flex-shrink-0">
                  <Clock className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-amber-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base sm:text-2xl font-bold text-amber-900 truncate">{summary.last24Hours}</p>
                  <p className="text-[9px] sm:text-xs text-amber-600">24h</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100 overflow-hidden min-w-0">
            <CardContent className="p-2 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-200 rounded-lg flex-shrink-0">
                  <Plus className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-green-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base sm:text-2xl font-bold text-green-900 truncate">{summary.byAction?.create || 0}</p>
                  <p className="text-[9px] sm:text-xs text-green-600">Created</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden min-w-0">
            <CardContent className="p-2 sm:p-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-200 rounded-lg flex-shrink-0">
                  <Edit className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-blue-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base sm:text-2xl font-bold text-blue-900 truncate">{summary.byAction?.update || 0}</p>
                  <p className="text-[9px] sm:text-xs text-blue-600">Updated</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters - 2 column grid */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <CardContent className="p-2 sm:p-4">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-3.5 h-3.5 text-gray-500 dark:text-muted-foreground flex-shrink-0" />
            <span className="text-[11px] sm:text-sm font-medium text-gray-700 dark:text-foreground">Filters:</span>
          </div>
          <div className="grid grid-cols-2 gap-2 w-full">
            <Select value={filterEntity} onValueChange={setFilterEntity}>
              <SelectTrigger className="h-8 sm:h-9 text-[11px] sm:text-sm w-full">
                <SelectValue placeholder="Entity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="booking">Bookings</SelectItem>
                <SelectItem value="lead">Leads</SelectItem>
                <SelectItem value="venue">Venues</SelectItem>
                <SelectItem value="inventory">Inventory</SelectItem>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="account_head">Account Head</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="h-8 sm:h-9 text-[11px] sm:text-sm w-full">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Created</SelectItem>
                <SelectItem value="update">Updated</SelectItem>
                <SelectItem value="delete">Deleted</SelectItem>
                <SelectItem value="payment">Payments</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Activity List */}
      <Card className="border-0 shadow-md overflow-hidden">
        <CardHeader className="pb-1 px-2 sm:px-6 pt-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-foreground truncate flex-1 min-w-0">Recent Activity</CardTitle>
          <span className="text-[10px] sm:text-sm text-gray-500 dark:text-muted-foreground flex-shrink-0 ml-2">{logs.length} entries</span>
        </CardHeader>
        <CardContent className="px-2 sm:px-6 pb-3 overflow-hidden">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse flex items-start gap-4 p-4 bg-gray-50 dark:bg-secondary rounded-lg">
                  <div className="w-10 h-10 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-muted-foreground text-lg">No activity logs yet</p>
              <p className="text-gray-400 dark:text-muted-foreground text-sm mt-1">Activities will appear here when your team makes changes</p>
            </div>
          ) : (
            <div className="space-y-1.5 sm:space-y-3 max-h-[500px] overflow-y-auto overflow-x-hidden w-full">
              {logs.map((log) => (
                <div 
                  key={log.id} 
                  className="flex items-start gap-1.5 sm:gap-2 p-2 sm:p-4 bg-gray-50 dark:bg-secondary hover:bg-gray-100 dark:bg-secondary rounded-lg transition-colors group overflow-hidden w-full cursor-pointer"
                  onClick={() => handleActivityClick(log)}
                  data-testid={`activity-item-${log.id}`}
                >
                  {/* Action Icon */}
                  <div className="p-1 sm:p-2 bg-white dark:bg-card rounded-full shadow-sm flex-shrink-0">
                    {getActionIcon(log.action)}
                  </div>
                  
                  {/* Content - Properly constrained width using w-0 flex-1 trick */}
                  <div className="flex-1 w-0 overflow-hidden">
                    {/* User info row */}
                    <div className="flex items-center gap-1 mb-0.5 flex-wrap">
                      <span className="font-medium text-[11px] sm:text-sm text-gray-900 dark:text-foreground truncate max-w-[60px] sm:max-w-[120px]">{log.userName}</span>
                      <Badge className={`${getRoleBadge(log.userRole)} text-[7px] sm:text-xs px-1 py-0 flex-shrink-0`}>
                        {log.userRole}
                      </Badge>
                      <Badge className={`${getActionBadge(log.action)} text-[7px] sm:text-xs px-1 py-0 flex-shrink-0`}>
                        {log.action}
                      </Badge>
                    </div>
                    
                    {/* Entity info - truncate long names */}
                    <p className="text-[10px] sm:text-sm text-gray-700 dark:text-foreground truncate">
                      <span className="capitalize">{log.entityType}:</span>
                      {' '}<span className="font-medium">{log.entityName}</span>
                    </p>
                    
                    {/* Details - wrap text properly with line clamp */}
                    {log.details && (
                      <p className="text-[9px] sm:text-xs text-gray-500 dark:text-muted-foreground mt-0.5 line-clamp-2 break-words">
                        {log.details}
                      </p>
                    )}
                    
                    {/* Timestamp and View hint */}
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-[9px] sm:text-xs text-gray-400 dark:text-muted-foreground">
                        {formatDateTime(log.createdAt)}
                      </p>
                      <span className="text-[8px] sm:text-xs text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                        <Eye className="w-3 h-3" /> View details
                      </span>
                    </div>
                  </div>
                  
                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLog(log.id);
                    }}
                    disabled={deleting === log.id}
                    className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50 dark:bg-red-900/20 h-6 w-6 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                  >
                    {deleting === log.id ? (
                      <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                    ) : (
                      <X className="w-3 h-3 sm:w-4 sm:h-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Info className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 flex-shrink-0" />
              Activity Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedActivity && (
            <div className="space-y-3 sm:space-y-4 overflow-hidden">
              {/* Action & Entity Header */}
              <div className="flex items-start gap-2 p-2 sm:p-3 bg-gray-50 dark:bg-secondary rounded-lg overflow-hidden">
                <div className="p-1.5 sm:p-2 bg-white dark:bg-card rounded-full shadow-sm flex-shrink-0">
                  {getActionIcon(selectedActivity.action)}
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <Badge className={`${getActionBadge(selectedActivity.action)} text-[10px] sm:text-xs`}>
                      {selectedActivity.action}
                    </Badge>
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-muted-foreground capitalize">{selectedActivity.entityType}</span>
                  </div>
                  <p className="font-semibold text-sm sm:text-base text-gray-900 dark:text-foreground truncate">{selectedActivity.entityName}</p>
                </div>
              </div>

              {/* User Info */}
              <div className="border rounded-lg p-2 sm:p-3 space-y-1.5 sm:space-y-2">
                <h4 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-foreground flex items-center gap-1.5 sm:gap-2">
                  <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" /> Performed By
                </h4>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm sm:text-base text-gray-900 dark:text-foreground font-medium truncate">{selectedActivity.userName}</span>
                  <Badge className={`${getRoleBadge(selectedActivity.userRole)} text-[10px] sm:text-xs flex-shrink-0`}>
                    {selectedActivity.userRole}
                  </Badge>
                </div>
                {selectedActivity.userEmail && (
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-muted-foreground flex items-center gap-1 truncate">
                    <Mail className="w-3 h-3 flex-shrink-0" /> {selectedActivity.userEmail}
                  </p>
                )}
              </div>

              {/* Timestamp */}
              <div className="border rounded-lg p-2 sm:p-3">
                <h4 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-foreground flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" /> Date & Time
                </h4>
                <p className="text-sm sm:text-base text-gray-900 dark:text-foreground">{formatDateTime(selectedActivity.createdAt)}</p>
              </div>

              {/* Details */}
              {selectedActivity.details && (
                <div className="border rounded-lg p-2 sm:p-3">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-foreground flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" /> Description
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground whitespace-pre-wrap break-words">
                    {selectedActivity.details}
                  </p>
                </div>
              )}

              {/* Changes (if available) */}
              {selectedActivity.changes && (
                <div className="border rounded-lg p-2 sm:p-3 overflow-hidden">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-foreground flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" /> Changes Made
                  </h4>
                  <div className="overflow-x-auto">
                    {formatChanges(selectedActivity.changes)}
                  </div>
                </div>
              )}

              {/* Metadata (if available) */}
              {selectedActivity.metadata && Object.keys(selectedActivity.metadata).length > 0 && (
                <div className="border rounded-lg p-2 sm:p-3">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 dark:text-foreground flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" /> Additional Information
                  </h4>
                  <div className="space-y-1 text-xs sm:text-sm">
                    {Object.entries(selectedActivity.metadata).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-0.5 sm:py-1 border-b border-gray-100 dark:border-border last:border-0 gap-2">
                        <span className="text-gray-500 dark:text-muted-foreground capitalize flex-shrink-0">{key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}</span>
                        <span className="text-gray-900 dark:text-foreground font-medium text-right truncate max-w-[50%]">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Entity ID */}
              <div className="text-[10px] sm:text-xs text-gray-400 dark:text-muted-foreground pt-2 border-t break-all">
                <p>Activity ID: {selectedActivity.id}</p>
                {selectedActivity.entityId && <p>Entity ID: {selectedActivity.entityId}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivityLog;
